import { Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';

export type PlatformAnalyticsQuery = Record<string, string | undefined>;

const DEFAULT_DAYS = 30;
const MAX_DAYS = 400;

/**
 * Analytics for the platform operator, not for a merchant.
 *
 * The platform's product is *merchant success*, so its metrics are about the
 * merchant lifecycle — activation, churn, cost to serve — not about shoppers.
 * Nothing here is store-scoped, which is exactly why it must sit behind
 * platform-only roles.
 *
 * Reads `platform.merchant_stats_daily`, one row per store per day, so a
 * thousand merchants is a thousand rows a day rather than millions of events.
 */
@Injectable()
export class PlatformAnalyticsService {
  constructor(private readonly prisma: PrismaService) {}

  private days(query: PlatformAnalyticsQuery): number {
    const raw = Number(query.days ?? DEFAULT_DAYS);
    return Number.isFinite(raw) ? Math.min(Math.max(Math.floor(raw), 1), MAX_DAYS) : DEFAULT_DAYS;
  }

  /** BIGINT → Number; JSON.stringify throws on BigInt rather than skipping it. */
  private num(value: unknown): number {
    return typeof value === 'bigint' ? Number(value) : Number(value ?? 0);
  }

  /** GMV, orders and active merchants across the whole platform. */
  async overview(query: PlatformAnalyticsQuery) {
    const days = this.days(query);

    const [totals] = await this.prisma.$queryRawUnsafe<
      { gmv: bigint; orders: bigint; sellingStores: bigint; activeStores: bigint }[]
    >(
      // Joined to store.stores rather than counting distinct storeIds in the
      // rollup: the rollup also carries rows for stores that have since been
      // deactivated or deleted, which produced the nonsense of "5 active out
      // of 3 total".
      `SELECT COALESCE(sum(m."gmv"), 0)    AS gmv,
              COALESCE(sum(m."orders"), 0) AS orders,
              count(DISTINCT m."storeId") FILTER (WHERE m."orders" > 0) AS "sellingStores",
              count(DISTINCT m."storeId")  AS "activeStores"
         FROM platform.merchant_stats_daily m
         JOIN store.stores s ON s."id" = m."storeId" AND s."isActive" = TRUE
        WHERE m."day" >= current_date - $1::int`,
      days
    );

    const [stores] = await this.prisma.$queryRawUnsafe<{ total: bigint }[]>(
      `SELECT count(*) AS total FROM store.stores WHERE "isActive" = TRUE`
    );

    const total = this.num(stores?.total);
    const selling = this.num(totals?.sellingStores);

    return {
      days,
      gmv: this.num(totals?.gmv),
      orders: this.num(totals?.orders),
      totalStores: total,
      // A store doing *something* vs a store actually selling. The gap between
      // these two is the platform's real problem at any given time.
      activeStores: this.num(totals?.activeStores),
      sellingStores: selling,
      sellingShare: total ? Math.round((selling / total) * 1000) / 10 : 0,
    };
  }

  /**
   * The activation funnel — the platform's single most important number.
   *
   * Signing merchants up is worthless if they never reach a first order, and
   * this is the only view that shows where they stall. Each step is a strict
   * subset of the one above it.
   */
  async activation() {
    const [row] = await this.prisma.$queryRawUnsafe<
      {
        created: bigint;
        withProduct: bigint;
        withBranding: bigint;
        withOrder: bigint;
        withTenOrders: bigint;
      }[]
    >(
      `SELECT
         count(*) AS created,
         count(*) FILTER (WHERE EXISTS (
           SELECT 1 FROM products.products p WHERE p."storeId" = s."id" AND p."isActive"
         )) AS "withProduct",
         count(*) FILTER (WHERE EXISTS (
           SELECT 1 FROM store.store_branding b WHERE b."storeId" = s."id"
         )) AS "withBranding",
         count(*) FILTER (WHERE EXISTS (
           SELECT 1 FROM sales.orders o WHERE o."storeId" = s."id"
         )) AS "withOrder",
         count(*) FILTER (WHERE (
           SELECT count(*) FROM sales.orders o WHERE o."storeId" = s."id"
         ) >= 10) AS "withTenOrders"
       FROM store.stores s
      WHERE s."isActive" = TRUE`
    );

    const created = this.num(row?.created);
    const steps = [
      { key: 'created', label: 'Store created', stores: created },
      { key: 'branded', label: 'Branding set up', stores: this.num(row?.withBranding) },
      { key: 'catalogued', label: 'First product added', stores: this.num(row?.withProduct) },
      { key: 'selling', label: 'First order received', stores: this.num(row?.withOrder) },
      { key: 'established', label: '10+ orders', stores: this.num(row?.withTenOrders) },
    ];

    return {
      steps: steps.map((step) => ({
        ...step,
        share: created ? Math.round((step.stores / created) * 1000) / 10 : 0,
      })),
    };
  }

  /**
   * Stores that were selling and have stopped.
   *
   * Deliberately not "stores with no orders ever" — that is an activation
   * problem, already covered above. This is the population worth a phone call:
   * they proved the product works for them, and then went quiet.
   */
  async churnRisk(query: PlatformAnalyticsQuery) {
    const idleDays = Math.min(Math.max(Number(query.idleDays ?? 30), 7), 180);

    const rows = await this.prisma.$queryRawUnsafe<
      {
        storeId: string;
        name: string | null;
        lastOrderAt: Date | null;
        idleDays: number;
        lifetimeOrders: bigint;
        lifetimeGmv: bigint;
      }[]
    >(
      `SELECT s."id" AS "storeId", s."name",
              max(o."createdOn") AS "lastOrderAt",
              (current_date - max(o."createdOn")::date)::int AS "idleDays",
              count(o."id") AS "lifetimeOrders",
              COALESCE(sum(o."grandTotal"), 0) AS "lifetimeGmv"
         FROM store.stores s
         JOIN sales.orders o ON o."storeId" = s."id"
        WHERE s."isActive" = TRUE
        GROUP BY s."id", s."name"
       HAVING max(o."createdOn") < now() - ($1::int || ' days')::interval
        ORDER BY COALESCE(sum(o."grandTotal"), 0) DESC
        LIMIT 50`,
      idleDays
    );

    return {
      idleDays,
      items: rows.map((r) => ({
        storeId: r.storeId,
        name: r.name,
        lastOrderAt: r.lastOrderAt,
        idleDays: this.num(r.idleDays),
        lifetimeOrders: this.num(r.lifetimeOrders),
        // Ordered by lifetime value, so the most expensive silence is at the top.
        lifetimeGmv: this.num(r.lifetimeGmv),
      })),
    };
  }

  /**
   * Cost to serve, per merchant.
   *
   * Very few platforms can compute this per tenant; we can, because the audit
   * layer already records API volume, error rate and audit-row volume per
   * store. It answers two questions nothing else does: which merchants are
   * unprofitable, and which plan tiers are mispriced.
   *
   * `apiCalls` is already sample-corrected by the rollup — without that, a
   * read-heavy merchant would look ~100x cheaper than they are, which is
   * precisely the population this exists to find.
   */
  async costToServe(query: PlatformAnalyticsQuery) {
    const days = this.days(query);

    const rows = await this.prisma.$queryRawUnsafe<
      {
        storeId: string;
        name: string | null;
        apiCalls: bigint;
        apiErrors: bigint;
        auditRows: bigint;
        sessions: bigint;
        orders: bigint;
        gmv: bigint;
        storageBytes: bigint;
      }[]
    >(
      `SELECT m."storeId", s."name",
              COALESCE(sum(m."apiCalls"), 0)  AS "apiCalls",
              COALESCE(sum(m."apiErrors"), 0) AS "apiErrors",
              COALESCE(sum(m."auditRows"), 0) AS "auditRows",
              COALESCE(sum(m."sessions"), 0)  AS sessions,
              COALESCE(sum(m."orders"), 0)    AS orders,
              COALESCE(sum(m."gmv"), 0)       AS gmv,
              COALESCE(max(m."storageBytes"), 0) AS "storageBytes"
         FROM platform.merchant_stats_daily m
         LEFT JOIN store.stores s ON s."id" = m."storeId"
        WHERE m."day" >= current_date - $1::int
        GROUP BY m."storeId", s."name"
        ORDER BY COALESCE(sum(m."apiCalls"), 0) DESC
        LIMIT 50`,
      days
    );

    return {
      days,
      items: rows.map((r) => {
        const apiCalls = this.num(r.apiCalls);
        const orders = this.num(r.orders);
        const gmv = this.num(r.gmv);

        return {
          storeId: r.storeId,
          name: r.name,
          apiCalls,
          apiErrors: this.num(r.apiErrors),
          auditRows: this.num(r.auditRows),
          sessions: this.num(r.sessions),
          orders,
          gmv,
          storageBytes: this.num(r.storageBytes),
          // The ratio that matters: consumption per unit of value produced. A
          // merchant with heavy traffic and no orders costs money and earns
          // none — that is a support conversation, or a pricing change.
          callsPerOrder: orders ? Math.round(apiCalls / orders) : null,
          revenuePerCall: apiCalls ? Math.round((gmv / apiCalls) * 100) / 100 : 0,
        };
      }),
    };
  }

  /**
   * Which stores are erroring, worst first.
   *
   * Finds a merchant having a bad week before they open a support ticket, which
   * is the difference between "they told us" and "we told them".
   */
  async reliability(query: PlatformAnalyticsQuery) {
    const days = Math.min(Math.max(Number(query.days ?? 7), 1), 90);

    const rows = await this.prisma.$queryRawUnsafe<
      { storeId: string; name: string | null; apiCalls: bigint; apiErrors: bigint }[]
    >(
      `SELECT m."storeId", s."name",
              COALESCE(sum(m."apiCalls"), 0)  AS "apiCalls",
              COALESCE(sum(m."apiErrors"), 0) AS "apiErrors"
         FROM platform.merchant_stats_daily m
         LEFT JOIN store.stores s ON s."id" = m."storeId"
        WHERE m."day" >= current_date - $1::int
        GROUP BY m."storeId", s."name"
       HAVING COALESCE(sum(m."apiErrors"), 0) > 0
        ORDER BY COALESCE(sum(m."apiErrors"), 0) DESC
        LIMIT 25`,
      days
    );

    return {
      days,
      items: rows.map((r) => {
        const calls = this.num(r.apiCalls);
        const errors = this.num(r.apiErrors);

        return {
          storeId: r.storeId,
          name: r.name,
          apiCalls: calls,
          apiErrors: errors,
          errorRate: calls ? Math.round((errors / calls) * 10000) / 100 : 0,
        };
      }),
    };
  }

  /** GMV per day across the platform — the one time series this page owns. */
  async trend(query: PlatformAnalyticsQuery) {
    const days = this.days(query);

    const rows = await this.prisma.$queryRawUnsafe<
      { day: Date; gmv: bigint; orders: bigint; stores: bigint }[]
    >(
      `SELECT "day",
              COALESCE(sum("gmv"), 0)    AS gmv,
              COALESCE(sum("orders"), 0) AS orders,
              count(DISTINCT "storeId") FILTER (WHERE "orders" > 0) AS stores
         FROM platform.merchant_stats_daily
        WHERE "day" >= current_date - $1::int
        GROUP BY "day"
        ORDER BY "day" ASC`,
      days
    );

    return {
      days,
      points: rows.map((r) => ({
        day: r.day,
        gmv: this.num(r.gmv),
        orders: this.num(r.orders),
        sellingStores: this.num(r.stores),
      })),
    };
  }
}
